import { Node, Transforms, Editor, ReactEditor, useSlateStatic, useSlateSelection } from '../core';
import { BaseEditor } from './Editable';
import type { SlateContent } from '../types';
import type { CursorStyle, LineIndicator as LineIndicatorType } from 'generated/graphql';
import { usePlugins, NodeStateProvider, ReporterProvider, RangeMasksProvider } from '../hooks';
import type { AllReporterVariant, PluginsBag } from '../hooks';
import { useEffect, Component, Suspense, useCallback, useLayoutEffect } from 'react';
import { HoveringToolbar } from './HoveringToolbar';
import { PortalToolbarButton } from './PortalToolbar';
import { safeDeselect, partialEditor } from '../utils';
import { ReporterDevTools } from '../../Reporter/ReporterDevTools';
import analytics from 'modules/analytics';
import { ReporterOpsLogger } from './ReporterOpsLogger';
import { env } from 'config/env';
import { Renderer } from '../renderer';
import { PARAGRAPH_PLUGIN_ID } from '../plugins';
import {
  performDefaultSelection,
  useRichTextEditorDefaultSelection,
} from 'hooks/useRichTextEditorDefaultSelection';
import { logger } from 'modules/logger';
import { useHaveRichTextEditorFeatureFlagsLoaded } from 'hooks/useHaveRichTextEditorFeatureFlagsLoaded';
import { useUnifiedEditorSizes } from './UnifiedEditor';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { CursorIndicator } from './CursorIndicator';
import { LineIndicator } from './LineIndicator';
import { useRecorder } from 'common/Recorder/useRecorder';
import { AutoGenerateImpressions } from '../../Reporter/ImpressionGenerator/AutoGenerateImpressions';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { RichTextEditorLoading } from './RichTextEditorLoading';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlateContext } from 'domains/reporter/Reporter/SlateContext';
import { useLineIndicator } from '../hooks/useLineIndicator';

export type RichTextEditorComponentProps = Readonly<{
  initialValue: SlateContent;
  onChange: (value: Array<Node>) => void;
  editor?: Nullable<Editor>;
  /**
   * Apply the default selection for the reporter. Otherwise the starting selection will be null.
   *
   * @default true
   */
  enableDefaultSelection?: boolean;
  /**
   * Toggles the hovering toolbar functionality.
   *
   * @default true
   */
  enableHoveringToolbar?: boolean;
  /**
   * Apply style to the contenteditable
   */
  style?: Record<any, any>;
  /**
   * Apply cursor style settings
   */
  cursorStyle?: CursorStyle;
  /**
   * Apply line indicator settings
   */
  lineIndicator?: LineIndicatorType;
  /**
   * Called onMount after the Slate singleton has been created.
   */
  onInit?: (props: { editor: Editor; pluginsBag: PluginsBag }) => void;
  /**
   * Called when unmounting to unreference the Slate editor and plugins.
   * This is an important clean-up step to prevent the Slate context from possessing a stale editor,
   * in case this component is recreated without a page refresh.
   */
  onDestroy?: () => void;
  /**
   * Used to decide if a default selection should be made on the new editor.
   * If a template is being autoloaded, defer to that process,
   * since attempting to make a selection may conflict with it.
   */
  autoloadTemplateState?: AutoloadTemplateState;
  variant: AllReporterVariant;
  placeholder?: Nullable<string>;
  isAddendumEditor?: boolean;
  isLoading?: boolean;
}>;

export class ErrorBoundary extends Component<{
  editor: Editor;
  ignoreMergeFields?: boolean;
  children: React.ReactNode;
}> {
  componentDidCatch(error: Error, errorInfo: unknown) {
    logger.error(error, errorInfo);

    // If we get an error from inside Slate, reset its selection to the default, since this
    // is the most likely cause of the error (Cannot resolve a DOM point from Slate point)
    // NOTE: this will not hide the error overlay in development mode!!
    performDefaultSelection(this.props.editor, {
      ignoreMergeFields: this.props.ignoreMergeFields ?? false,
    });
    analytics.error(error);
  }
  render() {
    return this.props.children;
  }
}

function UpdateLineIndicator({ editor }: { editor?: Editor }) {
  const selection = useSlateSelection();
  const { updatePosition: updateLineIndicatorPosition } = useLineIndicator();

  useEffect(() => {
    updateLineIndicatorPosition({ selection, editor });
  }, [editor, selection, updateLineIndicatorPosition]);

  return null;
}

export const RichTextEditorComponent = ({
  onChange,
  isLoading,
  initialValue,
  enableDefaultSelection = true,
  enableHoveringToolbar = true,
  style,
  cursorStyle,
  lineIndicator,
  onInit,
  onDestroy,
  autoloadTemplateState,
  variant,
  placeholder = null,
  isAddendumEditor = false,
}: RichTextEditorComponentProps): React.ReactElement => {
  const sizes = useUnifiedEditorSizes();
  const pluginsBag = usePlugins();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  // tells us when the user clicks or types into the RichTextEditor
  const { userActivityElementRef, setTextEditorElement } = useRecorder();
  const ignoreMergeFields =
    data?.me?.reporterSettings?.mergeFieldsSettings?.ignoreDefaultSelection ?? false;
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);

  const editor = useSlateStatic();

  useRichTextEditorDefaultSelection(editor, {
    enableDefaultSelection,
    ignoreMergeFields,
    autoloadTemplateState,
  });

  const { registerChangeHandlers } = useSlateContext();
  const { updatePosition: updateLineIndicatorPosition } = useLineIndicator();

  // Focus editor on mount - useLayoutEffect runs synchronously before useEffect
  // This ensures editor is focused before onInit is called
  useLayoutEffect(() => {
    try {
      if (!enableDefaultSelection) return;
      ReactEditor.focus(editor);
    } catch (e: any) {
      logger.error(
        '[RichTextEditor] Error occurred when focusing the editor on load.',
        {
          editor: partialEditor(editor),
          ignoreMergeFields,
        },
        e
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // onInit runs once after focus completes - useLayoutEffect completes synchronously before useEffect runs
  useEffect(() => {
    onInit?.({ editor, pluginsBag });

    return () => {
      onDestroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValueChange = useCallback(
    (content: SlateContent) => {
      if (content.length === 0) {
        logger.info("[RichTextEditor] Editor's content is empty, adding a paragraph.");
        Transforms.insertNodes(editor, {
          type: PARAGRAPH_PLUGIN_ID,
          children: [{ text: '' }],
        });
        performDefaultSelection(editor, { ignoreMergeFields });
        onChange([{ type: PARAGRAPH_PLUGIN_ID, children: [{ text: '' }] }]);
        return;
      }
      onChange(content);
      // Update line indicator position since selection may have changed as a side effect
      // (e.g., pressing Enter moves cursor to new line but doesn't fire onSelectionChange)
      if (editor.selection != null) {
        updateLineIndicatorPosition({ selection: editor.selection, editor });
      }
    },
    [editor, ignoreMergeFields, onChange, updateLineIndicatorPosition]
  );

  useEffect(() => {
    // updates the onChange handler in the Slate context
    registerChangeHandlers({
      onValueChange,
    });
  }, [registerChangeHandlers, onValueChange]);

  return (
    <ErrorBoundary editor={editor} ignoreMergeFields={ignoreMergeFields}>
      <div ref={userActivityElementRef} css={{ display: 'flex', flex: '1', maxWidth: '100%' }}>
        <div ref={setTextEditorElement} css={{ display: 'flex', flex: '1', maxWidth: '100%' }}>
          {enableHoveringToolbar && <HoveringToolbar />}
          <PortalToolbarButton
            pluginID="placeholder"
            height={variant === 'fragment' ? '4.5rem' : '6rem'}
          />
          <PortalToolbarButton
            pluginID="list"
            height={variant === 'fragment' ? '4.5rem' : '6rem'}
          />
          {cursorStyle != null && sizes != null && (
            <CursorIndicator
              isAddendumEditor={isAddendumEditor}
              containerWidth={sizes.width}
              containerHeight={sizes.height}
              color={cursorStyle.color}
            />
          )}
          {lineIndicator != null && lineIndicator.enabled && sizes != null && (
            <LineIndicator
              containerWidth={sizes.width}
              containerHeight={sizes.height}
              variant={lineIndicator.variant}
              placement={lineIndicator.placement}
              editor={editor}
              isAddendumEditor={isAddendumEditor}
            />
          )}
          <BaseEditor
            css={`
              ${cursorStyle?.color != null ? `* { caret-color: transparent;}` : ''}
              ${isLoading === true ? 'display: none;' : ''}
                &:focus-visible {
                outline: none;
              }
            `}
            placeholder={placeholder ?? ''}
            style={style}
            autoFocus={enableDefaultSelection}
            onDrop={undefined}
            onDragStart={undefined}
          />
          {isLoading === true && <RichTextEditorLoading />}
          {variant === 'report' && isAutoImpressionsEnabled && <AutoGenerateImpressions />}
          {env.MODE === 'development' && (
            <Suspense fallback={<></>}>
              <ReporterDevTools />
            </Suspense>
          )}
          {env.NODE_ENV !== 'test' && <ReporterOpsLogger initialState={initialValue} />}
          <UpdateLineIndicator editor={editor} />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export type RichTextEditorProps = Readonly<
  RichTextEditorComponentProps & {
    editor?: Nullable<Editor>;
    variant: AllReporterVariant;
    isDisabled?: boolean;
    onReportChange?: (content: SlateContent) => void;
    isLoading?: boolean;
    /**
     * Run the browser in a headless manner. In this mode, the selection of the editor will never be set to null
     * unless you set it yourself using safeDeselect from utils. This allows for all operations to be ran under
     * the hood as expected and updates the selection as operations are applied.
     *
     * NON-HEADLESS MODE IS NOT FULLY SUPPORTED. YOU WILL LIKELY RUN INTO BUGS IF YOU USE IT DUE TO THE PICKLIST
     * AND INLINE BOOKMARK PLUGINS.
     *
     * @default true
     */
    headlessMode?: boolean;
    /**
     * A ref to point nav items to.
     */
    navMountPoint?: Nullable<HTMLElement>;
  }
>;

export const RichTextEditor = ({
  variant,
  navMountPoint,
  headlessMode = true,
  isAddendumEditor = false,
  isDisabled = false,
  initialValue,
  onInit,
  editor,
  onReportChange,
  isLoading = false,
  ...rest
}: RichTextEditorProps): React.ReactElement => {
  // Set this before bootstrapping the editor
  useEffect(() => {
    if (headlessMode) {
      Transforms.deselect = () => {};
    } else {
      Transforms.deselect = safeDeselect;
    }
  }, [headlessMode]);

  const featureFlagsLoaded = useHaveRichTextEditorFeatureFlagsLoaded();

  return (
    <ReporterProvider variant={variant} headlessMode={headlessMode} navMountPoint={navMountPoint}>
      <NodeStateProvider>
        <RangeMasksProvider>
          {featureFlagsLoaded === false ? (
            <RichTextEditorLoading />
          ) : isDisabled === true ? (
            <Renderer content={initialValue} />
          ) : (
            <RichTextEditorComponent
              variant={variant}
              initialValue={initialValue}
              onInit={onInit}
              isAddendumEditor={isAddendumEditor}
              editor={editor}
              isLoading={isLoading}
              {...rest}
            />
          )}
        </RangeMasksProvider>
      </NodeStateProvider>
    </ReporterProvider>
  );
};

RichTextEditor.displayName = 'RichTextEditor';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(RichTextEditor);
